import React, { useEffect, useState, useCallback, useRef } from 'react';
import { QuickDrawer } from 'shared/components';
import { useFormik } from 'formik';
import moment from 'moment';
import { validationSchema, rxAmount } from './validations';
import {
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  Tooltip,
  LinearProgress,
  Link,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Fade,
  Icon as MuiIcon,
  Snackbar,
} from '@mui/material';
import Autocomplete from '@mui/lab/Autocomplete';
import { Done as DoneIcon } from '@mui/icons-material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CurrencyField from 'shared/containers/form/CurrencyField';
import { ConfirmationDialog } from 'shared/components';
import numeral from 'numeral';
import {
  useNewInvestmentMutation,
  useUpdateInvestmentMutation,
  useDeleteInvestmentMutation,
  useNewInvitation,
  useDonationTags,
  useAllDonationTags,
  useContribution,
  useInvestmentInvitation,
  useInvestmentDates,
  usePortfolioById,
  extractTags,
} from 'shared/apollo';
import { isEqual } from 'lodash';
import InvitationPopup from '../InvitationPopup';
import {
  Container,
  Header,
  Body,
  Row,
  Col,
  ColButton,
  Table,
  TRow,
  TCol,
  Button,
  SummaryRow,
  StyledTabs,
  Tab,
  Contributions,
  GrayText,
  SaveChip,
  WarningChip,
  ErrorSummary,
  ErrorSummaryRow,
  ErrorSummaryHeader,
  ErrorSummaryDescription,
  ErrorSummaryGroup,
} from './styles';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import DueDate from '../DueDate';
import VariableTimeline from '../VariableTimeline';
import { Popper } from '../NewPortfolio/styles';
import styled from 'styled-components';
import { formattedDate } from 'shared/utils/date';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

const rxEmail = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line

const initialValues = {
  portfolio: 0,
  portfolioYear: null,
  manager: 0,
  cash: '',
  goods: '',
  volunteerism: '',
  services: '',
  programNameByDonor: '',
  tags: [],
  externalId: '',
};

const invitationInitialValues = {
  email: '',
  firstName: '',
  lastName: '',
  message: '',
  automatic: false,
  datetime: '',
  organization: '',
};

const getManagerId = (accounts, currentUserId) => {
  if (!accounts || accounts.length === 0) return 0;
  const index = accounts.findIndex(a => a.id === currentUserId);
  return index === -1 ? 0 : currentUserId;
};

const summaryTabs = [
  { id: 'details', title: 'Details' },
  { id: 'invitation', title: 'Invitation to report' },
];

const detailsFields = [
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'cash', label: 'Cash amount' },
  { key: 'goods', label: 'Goods value' },
  { key: 'volunteerism', label: 'Volunteerism amount' },
  { key: 'services', label: 'Services value' },
];

const invitationFields = [
  { key: 'organization', label: 'Organization name' },
  { key: 'firstName', label: 'Recipient first name' },
  { key: 'lastName', label: 'Recipient last name' },
  { key: 'email', label: 'Recipient email' },
];

const noDeadlineText = 'No Deadline';
const portfolioDateNumbers = [1, 2, 3, 4, 5, 6, 7, 8];
const getPortfolioDates = (portfolio, year) => {
  const portfolioDates = {};
  portfolioDateNumbers.forEach(x => {
    portfolioDates[x] = noDeadlineText;
  });
  if (!portfolio || !year || !portfolio.years || !portfolio.years.length)
    return portfolioDates;
  const fullYear = parseInt(year); // year could be string
  const portfolioYear = portfolio.years.find(x => x.year === fullYear);
  if (!portfolioYear) return portfolioDates;
  portfolioDateNumbers.forEach(x => {
    portfolioDates[x] =
      formattedDate(portfolioYear[`report_date_${x}`]) || portfolioDates[x];
  });
  return portfolioDates;
};

export const TIMELINE_CHOICES = [
  { id: 'invitation_sent', name: 'Invitation sent' },
  { id: 'allocate_investment', name: 'Allocate investment' },
  { id: 'submit_for_feedback', name: 'Submit for feedback' },
  { id: 'resolve_feedback', name: 'Resolve feedback' },
  { id: 'publish_report', name: 'Publish report' },
  { id: 'interim_report_update', name: 'Interim report update' },
  { id: 'finalize_report', name: 'Finalize report' },
];

const dateNames = [
  'Allocate Investment',
  'Submit For Feedback',
  'Resolve Feedback',
  'Publish Report',
  'Interim Report Update',
  'Finalize Report',
];

const parseDate = (data, date, prefix) => {
  return (
    data[`${prefix}report_date_${date}`] ||
    (data[`${prefix}timeline_from_${date}`] &&
    data[`${prefix}timeline_days_${date}`]
      ? TIMELINE_CHOICES.find(
          TC => TC.id === data[`${prefix}timeline_from_${date}`]?.toLowerCase(),
        )?.name +
        ' + ' +
        data[`${prefix}timeline_days_${date}`] +
        ' days'
      : noDeadlineText)
  );
};

const getDateRow = (name, inv, number) => ({
  name,
  date: parseDate(inv, number, ''),
  portfolioDate: parseDate(inv, number, 'portfolio_'),
  number,
});

const getDateRows = inv =>
  dateNames.map((name, index) => getDateRow(name, inv, index + 1));

const ContributionDrawer = ({
  open,
  onClose,
  portfolios,
  accounts,
  donationInitialValues,
  me,
  refresh,
}) => {
  const { user } = useUserActions();
  const mainPortfolios = portfolios.filter(({ isMain }) => isMain);
  // Build a flat list of (main portfolio, year) combos so the UI can offer a
  // single 'Name (Year)' dropdown while internally we keep the legacy
  // (mainPortfolioId, year) pair on the form state for retrocompatibility.
  const portfolioOptions = mainPortfolios.flatMap(m =>
    Object.entries(m.yearIdentifier || {}).map(([year, yearlyId]) => ({
      yearlyId,
      mainId: m.id,
      name: m.name,
      year: parseInt(year, 10),
      isImpactSnapshot: m.isImpactSnapshot ?? false,
    })),
  );
  const { data: investmentDates } = useInvestmentDates(
    donationInitialValues?.id,
  );
  const { run: runNew, isLoading: loadingNew } = useNewInvestmentMutation();
  const {
    run: runUpdate,
    isLoading: loadingUpdate,
  } = useUpdateInvestmentMutation();
  const {
    run: runDelete,
    isLoading: loadingDelete,
  } = useDeleteInvestmentMutation();
  const { run: runInvitation } = useNewInvitation();
  const { data: currentInvitations } = useInvestmentInvitation(
    donationInitialValues?.id,
  );
  const [donationId, setDontationId] = useState(donationInitialValues?.id);
  const [finished, setFinished] = useState(false);
  const [invitationFinished, setInvitationFinished] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [dialogDismiss, setDialogDismiss] = useState(false);
  const [organization, setOrganization] = useState('');
  const [invitationStatus, setInvitationStatus] = useState();
  const [invitationSent, setInvitationSent] = useState(false);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState();
  const [startValues, setStartValues] = useState(initialValues);
  const [errorOnInvitation, setErrorOnInvitation] = useState(false);
  const [invitationWarning, setInvitationWarning] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const { data: tagList, refresh: refreshTags } = useDonationTags(donationId);
  const { data: tagOptions } = useAllDonationTags();
  const { data: contribution } = useContribution(donationId);
  const disabledAmounts =
    donationInitialValues?.status === 'Allocated' ?? false;
  const childRef = useRef();
  const {
    portfolio: selectedPortfolio,
    refresh: portfolioSelectRefresh,
  } = usePortfolioById(selectedPortfolioId);

  useEffect(() => {
    const id = donationInitialValues?.id;
    if (!id) return;
    setDontationId(id);
    refreshTags();
  }, [donationInitialValues?.id]);

  useEffect(() => {
    if (!selectedPortfolioId) return;
    portfolioSelectRefresh();
  }, [selectedPortfolioId]);

  const parseStatus = (date, status = 'scheduled') => {
    return (
      `${status.charAt(0).toUpperCase() +
        status.substring(1).toLowerCase()} - ${moment(date).format('LLL')}` ||
      donationInitialValues.invitationStatus ||
      'Pending'
    );
  };

  const validateInvitation = values => {
    const errors = {};
    if (!values.email) {
      errors.email = 'A contact email is required';
    }
    if (!values.organization) {
      errors.organization = 'An organization name is required';
    }
    if (!values.firstName) {
      errors.firstName = 'A contact first name is required';
    }
    if (!values.lastName) {
      errors.lastName = 'A contact last name is required';
    }
    if (!values.message) {
      errors.message = 'Write a message';
    }

    const isValidEmail = rxEmail.test(values.email);

    if (values.email && !isValidEmail) {
      errors.email = 'Invalid email address';
    }

    if (
      values.email &&
      isValidEmail &&
      window.location.hostname === 'uat.trueimpact.com'
    ) {
      const emailParts = values.email.split('@');
      const domain = emailParts[1];

      if (domain !== 'trueimpact.com') {
        errors.email = 'Invalid email for UAT environment';
      }
    }

    return errors;
  };

  const invitationValuesRef = useRef(invitationInitialValues);

  const onFinishNew = resourceId => {
    setDontationId(resourceId);
    setFinished(true);
    setTimeout(() => {
      refresh();
      refreshTags();
      setDontationId(resourceId);
      setFinished(false);
      /*if (childRef?.current?.runInvite) {
        childRef.current.runInvite();
      }*/
    }, 3000);
  };

  const onFinishUpdate = () => {
    setFinished(true);
    setTimeout(() => {
      refresh();
      refreshTags();
      setFinished(false);
    }, 2000);
  };

  const { values, handleSubmit, setFieldValue } = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      ...initialValues,
      manager: getManagerId(accounts, user.attributes['custom:user_id']),
    },
    onSubmit: values => {
      // Translate the (main portfolio, year) pair to the yearly portfolio id
      // expected by the backend (legacy data flow).
      const mainPortfolio = portfolios.find(p => p.id === values.portfolio);
      const portfolioId =
        mainPortfolio?.yearIdentifier?.[values.portfolioYear] ||
        values.portfolio;
      setStartValues(values);
      const form = {
        portfolioId,
        relationshipManagerId: values.manager,
        message: '',
        cashAmount: values.cash,
        goodsAmount: values.goods,
        volunteerismAmount: values.volunteerism,
        servicesAmount: values.services,
        programNameByDonor: values.programNameByDonor,
        externalId: values.externalId,
        tagList: JSON.stringify(values.tags),
        organization: invitationValuesRef.current.organization,
        firstName: invitationValuesRef.current.firstName,
        lastName: invitationValuesRef.current.lastName,
        email: invitationValuesRef.current.email,
        reportDate1: values.report_date_1,
        reportDate2: values.report_date_2,
        reportDate3: values.report_date_3,
        reportDate4: values.report_date_4,
        reportDate5: values.report_date_5,
        reportDate6: values.report_date_6,
        timelineDays1: values.timeline_days_1,
        timelineDays2: values.timeline_days_2,
        timelineDays3: values.timeline_days_3,
        timelineDays4: values.timeline_days_4,
        timelineDays5: values.timeline_days_5,
        timelineDays6: values.timeline_days_6,
        timelineFrom1: values.timeline_from_1,
        timelineFrom2: values.timeline_from_2,
        timelineFrom3: values.timeline_from_3,
        timelineFrom4: values.timeline_from_4,
        timelineFrom5: values.timeline_from_5,
        timelineFrom6: values.timeline_from_6,
      };
      setInvitationWarning(false);
      if (donationId) {
        runUpdate({ id: donationId, ...form }, onFinishUpdate);
      } else {
        runNew({ ...form }, onFinishNew);
      }
    },
    validate: validationSchema,
  });

  const [openInvitation, setOpenInvitation] = useState(false);

  const onFinishInvitation = data => {
    const result = data?.newInvitation;
    // Nothing was emailed: either a duplicate send, or the donation is already
    // fully allocated so the backend deliberately declined to send.
    if (result?.alreadySent || result?.error) {
      setInvitationSent(false);
      setSnackbarMessage(
        result?.error || 'This invitation has already been sent.',
      );
      setSnackbarOpen(true);
      return;
    }
    setInvitationFinished(true);
    setTimeout(() => {
      refresh();
      setInvitationFinished(false);
    }, 2000);
  };

  const {
    values: valuesInvitation,
    handleSubmit: submitInvitation,
    setFieldValue: setFieldInvitation,
    errors: errorsInvitation,
  } = useFormik({
    validateOnChange: false,
    initialValues: invitationInitialValues,
    onSubmit: values => {
      setOpenInvitation(false);
      const datetime = values.datetime
        ? moment.duration(moment(values.datetime).diff(moment())).asMinutes()
        : undefined;
      const form = {
        ...values,
        datetime: `${parseInt(datetime)}`,
        investmentId: donationId,
        organization: values.organization || organization,
      };
      if (values.automatic) setInvitationStatus(parseStatus(values.datetime));
      setInvitationSent(true);
      runInvitation(form, onFinishInvitation);
      handleSubmit();
    },
    validate: validateInvitation,
  });

  invitationValuesRef.current = valuesInvitation;

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const errors = submitAttempted ? validationSchema(values) : {};
  const errorsInvitationFields = submitAttempted
    ? (({ message, ...rest }) => rest)(validateInvitation(valuesInvitation))
    : {};
  const summaryErrors = [
    ...detailsFields
      .filter(({ key }) => errors[key])
      .map(f => ({
        ...f,
        tab: 'details',
        message: values[f.key] ? errors[f.key] : 'Required',
      })),
    ...invitationFields
      .filter(({ key }) => errorsInvitationFields[key])
      .map(f => ({
        ...f,
        tab: 'invitation',
        message: valuesInvitation[f.key]
          ? errorsInvitationFields[f.key]
          : 'Required',
      })),
  ];
  const tabHasError = tab => summaryErrors.some(e => e.tab === tab);
  const errorTabs = summaryTabs.filter(({ id }) => tabHasError(id));

  const handleSave = () => {
    setSubmitAttempted(true);
    const hasErrors =
      Object.keys(validationSchema(values)).length > 0 ||
      Object.keys(validateInvitation(valuesInvitation)).some(
        key => key !== 'message',
      );
    if (!hasErrors) handleSubmit();
  };

  const handleFix = (tab, key) => {
    setActiveTab(tab);
    setTimeout(() => {
      const el = document.getElementById(`field-${key}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const target = el.matches('input,textarea')
        ? el
        : el.querySelector('input,[role="button"]') || el;
      target.focus();
    }, 100);
  };

  const hasChanged = !isEqual(values, startValues);

  useEffect(() => {
    if (currentInvitations?.email) {
      setFieldInvitation('firstName', currentInvitations?.firstName);
      setFieldInvitation('organization', currentInvitations?.organization);
      setFieldInvitation('lastName', currentInvitations?.lastName);
      setFieldInvitation('email', currentInvitations?.email);
    }
  }, [currentInvitations]);

  useEffect(() => {
    if (accounts && accounts.length > 0 && !values.manager) {
      setFieldValue('manager', accounts[0].id);
      setStartValues({ ...values, manager: accounts[0].id });
    }
  }, [accounts]);

  // value is the yearly portfolio id selected in the unified dropdown.
  const handlePortfolioSelect = value => {
    const option = portfolioOptions.find(o => o.yearlyId === value);
    if (!option) {
      setFieldValue('portfolio', 0);
      setFieldValue('portfolioYear', null);
      setSelectedPortfolioId();
      return;
    }
    setFieldValue('portfolio', option.mainId);
    setFieldValue('portfolioYear', option.year);
    setSelectedPortfolioId(option.yearlyId);
  };

  useEffect(() => {
    const isEdit = Boolean(donationInitialValues?.id);
    if (donationInitialValues && !isEdit && donationInitialValues.portfolio) {
      handlePortfolioSelect(donationInitialValues.portfolio);
    }
    if (donationInitialValues && isEdit) {
      const { id, granteeName, portfolio, ...rest } = donationInitialValues;
      Object.keys(rest).forEach(key => {
        setFieldValue(key, rest[key]);
      });
      const selectedPortfolio = portfolios.filter(p => p.id === portfolio)[0];
      // Preserve the legacy form state: values.portfolio is the MAIN id and
      // values.portfolioYear is the year. selectedPortfolioId tracks the
      // yearly portfolio for date lookups.
      if (selectedPortfolio?.isMain) {
        setFieldValue('portfolio', selectedPortfolio.id);
      } else {
        setFieldValue('portfolio', selectedPortfolio?.mainPortfolioId);
      }
      setFieldValue('portfolioYear', selectedPortfolio?.year);
      setSelectedPortfolioId(selectedPortfolio?.id);
      ['cash', 'goods', 'volunteerism', 'services'].forEach(name => {
        setFieldValue(name, donationInitialValues[name]);
      });
      setDontationId(id);
      setOrganization(granteeName || '');
      setInvitationStatus(donationInitialValues.invitationStatus);
    }
  }, [donationInitialValues]);

  //update donation dates with db values
  useEffect(() => {
    if (investmentDates) {
      const rd1 = investmentDates.reportDate1;
      const rd2 = investmentDates.reportDate2;
      const rd3 = investmentDates.reportDate3;
      const rd4 = investmentDates.reportDate4;
      const rd5 = investmentDates.reportDate5;
      const rd6 = investmentDates.reportDate6;
      const td1 = investmentDates.timelineDays1;
      const td2 = investmentDates.timelineDays2;
      const td3 = investmentDates.timelineDays3;
      const td4 = investmentDates.timelineDays4;
      const td5 = investmentDates.timelineDays5;
      const td6 = investmentDates.timelineDays6;
      const tf1 = investmentDates.timelineFrom1;
      const tf2 = investmentDates.timelineFrom2;
      const tf3 = investmentDates.timelineFrom3;
      const tf4 = investmentDates.timelineFrom4;
      const tf5 = investmentDates.timelineFrom5;
      const tf6 = investmentDates.timelineFrom6;
      if (rd1) {
        setFieldValue('report_date_1', rd1);
      } else if (td1 && tf1) {
        setFieldValue('timeline_days_1', `${td1}`);
        setFieldValue('timeline_from_1', tf1.toLowerCase());
      }
      if (rd2) {
        setFieldValue('report_date_2', rd2);
      } else if (td2 && tf2) {
        setFieldValue('timeline_days_2', `${td2}`);
        setFieldValue('timeline_from_2', tf2.toLowerCase());
      }
      if (rd3) {
        setFieldValue('report_date_3', rd3);
      } else if (td3 && tf3) {
        setFieldValue('timeline_days_3', `${td3}`);
        setFieldValue('timeline_from_3', tf3.toLowerCase());
      }
      if (rd4) {
        setFieldValue('report_date_4', rd4);
      } else if (td4 && tf4) {
        setFieldValue('timeline_days_4', `${td4}`);
        setFieldValue('timeline_from_4', tf4.toLowerCase());
      }
      if (rd5) {
        setFieldValue('report_date_5', rd5);
      } else if (td5 && tf5) {
        setFieldValue('timeline_days_5', `${td5}`);
        setFieldValue('timeline_from_5', tf5.toLowerCase());
      }
      if (rd6) {
        setFieldValue('report_date_6', rd6);
      } else if (td6 && tf6) {
        setFieldValue('timeline_days_6', `${td6}`);
        setFieldValue('timeline_from_6', tf6.toLowerCase());
      }
    }
  }, [investmentDates]);

  useEffect(() => {
    if (!contribution) {
      return;
    }
    [1, 2, 3, 4, 5, 6].forEach(number => {
      setFieldValue(
        `report_date_${number}`,
        contribution[`reportDate${number}`],
      );
      setFieldValue(
        `timeline_days_${number}`,
        contribution[`timelineDays${number}`],
      );
      setFieldValue(
        `timeline_from_${number}`,
        contribution[`timelineFrom${number}`],
      );
    });
  }, [contribution]);

  useEffect(() => {
    if (tagList && tagList.length > 0) {
      setFieldValue(
        'tags',
        tagList.map(({ tag }) => tag),
      );
    }
  }, [tagList]);

  const onFinishDelete = () => {
    setDontationId(undefined);
    setFieldValue('porfolio', 0);
    setFieldValue('manager', 0);
    setFieldValue('cash', '');
    setFieldValue('goods', '');
    setFieldValue('volunteerism', '');
    setFieldValue('services', '');
    setFieldValue('tags', []);
    setFieldValue('externalId', '');
    onClose();
  };

  const handleDelete = () => {
    setDeleteDialog(false);
    runDelete({ id: donationId }, onFinishDelete);
  };

  const getTotal = () => {
    const { cash, goods, volunteerism, services } = values;
    return (
      (rxAmount.test(cash) ? parseFloat(cash) : 0) +
      (rxAmount.test(goods) ? parseFloat(goods) : 0) +
      (rxAmount.test(volunteerism) ? parseFloat(volunteerism) : 0) +
      (rxAmount.test(services) ? parseFloat(services) : 0)
    );
  };

  const handleDismiss = () => {
    if (hasChanged) {
      setDialogDismiss(true);
    } else {
      onClose();
    }
  };

  const handleCancelDismiss = () => {
    setDialogDismiss(false);
  };

  const handleConfirmDismiss = () => {
    onClose();
  };

  const total = `$${numeral(getTotal()).format(0, 0)}`;
  const isLoading = loadingNew || loadingUpdate || loadingDelete;
  const [activeTab, setActiveTab] = useState('details');
  const handleTabChange = useCallback((event, value) => {
    setActiveTab(value);
  }, []);

  const toggleInvitation = () => {
    if (!donationId) {
      setInvitationWarning(true);
      return;
    }
    setInvitationWarning(false);
    setOpenInvitation(state => !state);
  };

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDatePopper, setOpenDatePopper] = React.useState(false);
  const [placement, setPlacement] = React.useState();
  const [expanded, setExpanded] = React.useState('fixedDate');
  const [popperDateNumber, setPopperDateNumber] = useState();
  const [popperTitle, setPopperTitle] = useState();
  const handleDatePopper = (newPlacement, dateNumber, popperTitle) => event => {
    setAnchorEl(event.currentTarget);
    setPopperTitle(popperTitle);
    setOpenDatePopper(prev => placement !== newPlacement || !prev);
    setPlacement(newPlacement);
    setPopperDateNumber(dateNumber);
  };
  const handleChangeAccordion = useCallback(
    panel => (event, isExpanded) => {
      setExpanded(isExpanded ? panel : false);
    },
    [],
  );
  const onChangeTimelineDate = useCallback(
    value => {
      setFieldValue(`report_date_${popperDateNumber}`, value);
      setFieldValue(`timeline_days_${popperDateNumber}`, null);
      setFieldValue(`timeline_from_${popperDateNumber}`, null);
    },
    [popperDateNumber],
  );
  const onSave = useCallback(() => {
    setAnchorEl();
    setPlacement();
    setPopperDateNumber();
    setOpenDatePopper(false);
  }, [popperDateNumber]);
  const onChangeTimelineDays = useCallback(
    value => {
      setFieldValue(`report_date_${popperDateNumber}`, null);
      setFieldValue(`timeline_days_${popperDateNumber}`, value);
    },
    [popperDateNumber],
  );
  const onChangeTimelineFrom = useCallback(
    value => {
      setFieldValue(`report_date_${popperDateNumber}`, null);
      setFieldValue(`timeline_from_${popperDateNumber}`, value);
    },
    [popperDateNumber],
  );

  const invitationCreated =
    donationInitialValues && donationInitialValues?.invitationCreatedAt
      ? moment(
          donationInitialValues?.invitationCreatedAt,
          'MM/DD/YYYY, HH:mm:ss',
        ).format('DD MMM YYYY h:mm A')
      : 'Not Sent';

  const selectedPortfolioDates = getPortfolioDates(
    selectedPortfolio,
    values.portfolioYear,
  );

  return (
    <QuickDrawer open={open} onClose={handleDismiss} width="450px" title="">
      <InvitationPopup
        ref={childRef}
        onChangeMessage={message => setFieldInvitation('message', message)}
        open={openInvitation}
        onClose={toggleInvitation}
        errors={errorsInvitation}
        values={valuesInvitation}
        setField={setFieldInvitation}
        onSubmit={submitInvitation}
        investmentRecipientFirstName={valuesInvitation.firstName || 'Grantee'}
        investmentRecipientLastName={valuesInvitation.lastName || ''}
        investmentAmount={getTotal() || 0}
        investmentRecipientOrganization={
          valuesInvitation?.organization || organization || 'Organization'
        }
        investmentFundedProgramName={values?.programNameByDonor || 'Program'}
        portfolioYear={'-'}
        portfolioName={
          values.portfolio
            ? portfolios.filter(({ id }) => id === values.portfolio)[0]?.name
            : '-'
        }
        userFirst={me?.firstName}
        userLast={me?.lastName}
        userEmail={me?.email}
        userPhone={me?.phone || '-'}
        accountName={
          Array.isArray(accounts) && accounts.length > 0 && accounts[0]?.name
            ? accounts[0].name
            : 'A Donor'
        }
        submitForFeedback={values?.report_date_2 || 'your submit date'}
        resolveFeedback={values?.report_date_3 || 'your resolve date'}
        finalizeReportDate={values?.report_date_6 || 'your final date'}
        invitationSent={invitationSent}
        errorOnInvitation={errorOnInvitation}
      />
      <Container>
        <Popper
          open={openDatePopper}
          anchorEl={anchorEl}
          placement="top-end"
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <div>
                <div className="p-header">
                  <span className="h-title">{`Select ${popperTitle} due date`}</span>
                  <Icon>
                    <CloseIcon onClick={() => setOpenDatePopper(false)} />
                  </Icon>
                </div>
                <Accordion
                  expanded={expanded === 'fixedDate'}
                  onChange={handleChangeAccordion('fixedDate')}
                  style={{ textAlign: 'center' }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1bh-content"
                    id="panel1bh-header"
                  >
                    <span className="h-title">Fixed Date</span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <DueDate
                      year={values}
                      number={popperDateNumber}
                      onChangeTimelineDate={onChangeTimelineDate}
                      onSave={onSave}
                    />
                  </AccordionDetails>
                </Accordion>
                <Accordion
                  expanded={expanded === 'variableTimeline'}
                  onChange={handleChangeAccordion('variableTimeline')}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2bh-content"
                    id="panel2bh-header"
                  >
                    <span className="h-title">Variable Timeline</span>
                  </AccordionSummary>
                  <AccordionDetails>
                    <VariableTimeline
                      options={TIMELINE_CHOICES}
                      number={popperDateNumber}
                      onChangeTimelineDays={onChangeTimelineDays}
                      onChangeTimelineFrom={onChangeTimelineFrom}
                      onSave={onSave}
                    />
                  </AccordionDetails>
                </Accordion>
              </div>
            </Fade>
          )}
        </Popper>
        <Header>
          <Row className="mb15">
            <Col>
              <h2>Donation</h2>
            </Col>
            <Col flexEnd>
              <span className="h-price">{total}</span>
            </Col>
          </Row>
          {donationInitialValues && (
            <Row>
              <Col>
                <span className="h-title">
                  {donationInitialValues?.programName ?? 'Default Program'}
                </span>
              </Col>
            </Row>
          )}
          {donationInitialValues && (
            <Row className="mb15">
              <Col>
                <span className="h-sub">
                  {donationInitialValues?.programNameByDonor ||
                    'Donor program name not provided'}{' '}
                  - {donationInitialValues?.year}
                </span>
              </Col>
            </Row>
          )}
          {donationInitialValues && (
            <Row>
              <Table>
                <TRow>
                  <TCol style={{ borderLeft: 'none' }}>
                    <span className="r-title">Investment status:</span>
                    <span className="r-sub">
                      {donationInitialValues?.status}
                    </span>
                  </TCol>
                </TRow>
                <TRow>
                  <TCol>
                    <span className="r-title">Created:</span>
                    <span className="r-sub">
                      {invitationCreated || 'Not Sent'}
                    </span>
                  </TCol>
                  <TCol>
                    <span className="r-title">Sent:</span>
                    <span className="r-sub">
                      {invitationStatus &&
                      donationInitialValues?.status !== 'Unnotified'
                        ? invitationStatus.replace('Sent - ', '')
                        : 'Not Sent'}
                    </span>
                  </TCol>
                </TRow>
              </Table>
            </Row>
          )}
          <StyledTabs value={activeTab} onChange={handleTabChange}>
            <Tab
              value="details"
              label="Details"
              $hasError={tabHasError('details')}
              icon={tabHasError('details') ? <ErrorOutlineIcon /> : undefined}
              iconPosition="end"
            />
            <Tab
              className="last"
              value="invitation"
              label="Invitation to Report (Allocated)"
              $hasError={tabHasError('invitation')}
              icon={
                tabHasError('invitation') ? <ErrorOutlineIcon /> : undefined
              }
              iconPosition="end"
            />
          </StyledTabs>
        </Header>
        <Body>
          <Row>
            <span className="r-info-title">
              Enter individual donations below, or click{' '}
              <a
                onClick={() =>
                  window.open('/donor/investment-upload', '_blank')
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Upload File
              </a>{' '}
              to upload multiple donations.
            </span>
          </Row>
          {activeTab === 'details' && (
            <>
              <Row>
                <Col>
                  <span className="r-title">Portfolio:</span>
                </Col>
              </Row>
              <Row className="mb15">
                <Col>
                  <TextField
                    id="field-portfolio"
                    className="selector"
                    select
                    error={!!errors.portfolio}
                    helperText={errors.portfolio}
                    fullWidth
                    value={
                      (values.portfolio &&
                        portfolios.find(p => p.id === values.portfolio)
                          ?.yearIdentifier?.[values.portfolioYear]) ||
                      0
                    }
                    onChange={(_, { props }) =>
                      handlePortfolioSelect(props.value)
                    }
                  >
                    <MenuItem value={0}>Select a portfolio</MenuItem>
                    {portfolioOptions.map(item => (
                      <MenuItem key={item.yearlyId} value={item.yearlyId}>
                        {`${item.name} (${item.year})`}
                        {item.isImpactSnapshot && (
                          <Chip label="Impact Snapshot" size="small" style={{ marginLeft: 8 }} />
                        )}
                      </MenuItem>
                    ))}
                  </TextField>
                </Col>
              </Row>

              <Row>
                <Col>
                  <span className="r-title">Manager:</span>
                </Col>
              </Row>
              <Row className="mb15">
                <Col>
                  <TextField
                    className="selector"
                    error={errors.manager}
                    fullWidth
                    value={values.manager}
                    onChange={(_, { props }) => {
                      setFieldValue('manager', props.value);
                    }}
                    select
                  >
                    <MenuItem value={0}>Select a manager</MenuItem>
                    {accounts.map(item => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Col>
              </Row>

              <Row>
                <Col>
                  <span className="r-title">Tags:</span>
                  <Tooltip
                    title="Start typing tags. Hit tab, comma, or return to complete. Hit backspace/delete to remove."
                    arrow
                  >
                    <span className="tooltip">?</span>
                  </Tooltip>
                </Col>
              </Row>
              <Row className="mb15">
                <Col>
                  <Autocomplete
                    multiple
                    options={tagOptions}
                    defaultValue={[]}
                    freeSolo
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          variant="outlined"
                          label={option}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={params => (
                      <TextField {...params} variant="outlined" />
                    )}
                    value={extractTags(values.tags)}
                    onChange={(_, newTags) => {
                      setFieldValue('tags', newTags);
                    }}
                  />
                </Col>
              </Row>

              <Row>
                <Col>
                  <span className="r-title">External ID:</span>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                    value={values.externalId || ''}
                    onChange={e =>
                      setFieldValue('externalId', e.target.value)
                    }
                  />
                </Col>
              </Row>

              <Row>
                <Col paddingRight>
                  <span className="r-title">Cash amount:</span>
                  <CurrencyField
                    disabled={disabledAmounts}
                    inputComponent={TextField}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    id="field-cash"
                    value={values.cash}
                    onChange={e => setFieldValue('cash', e.target.value)}
                    error={errors.cash}
                    helperText={errors.cash}
                  />
                </Col>
                <Col paddingLeft>
                  <span className="r-title">Goods value:</span>
                  <CurrencyField
                    disabled={disabledAmounts}
                    inputComponent={TextField}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    id="field-goods"
                    value={values.goods}
                    onChange={e => setFieldValue('goods', e.target.value)}
                    error={errors.goods}
                    helperText={errors.goods}
                  />
                </Col>
              </Row>

              <Row className="mb15">
                <Col paddingRight>
                  <span className="r-title">Volunteerism amount:</span>
                  <CurrencyField
                    disabled={disabledAmounts}
                    inputComponent={TextField}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    fullWidth
                    id="field-volunteerism"
                    value={values.volunteerism}
                    onChange={e =>
                      setFieldValue('volunteerism', e.target.value)
                    }
                    error={errors.volunteerism}
                    helperText={errors.volunteerism}
                  />
                </Col>
                <Col paddingLeft>
                  <span className="r-title">Services value:</span>
                  <CurrencyField
                    disabled={disabledAmounts}
                    inputComponent={TextField}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    fullWidth
                    id="field-services"
                    value={values.services}
                    onChange={e => setFieldValue('services', e.target.value)}
                    error={errors.services}
                    helperText={errors.services}
                  />
                </Col>
              </Row>
              {donationInitialValues?.allocations &&
                donationInitialValues?.allocations.length > 0 && (
                  <Row className="mb15">
                    <Col>
                      <span className="h-title">
                        Donation total:{' '}
                        <GrayText>
                          $
                          {donationInitialValues.allocations.reduce(
                            (acc, item) => {
                              acc += item.amount;
                              return acc;
                            },
                            0,
                          )}
                        </GrayText>
                      </span>
                    </Col>
                  </Row>
                )}
              {donationInitialValues?.allocations &&
                donationInitialValues?.allocations.length > 0 && (
                  <Contributions>
                    <ul>
                      {donationInitialValues.allocations.map(
                        ({ name, amount }) => (
                          <li key={name}>
                            {name}
                            <GrayText>${amount}</GrayText>
                          </li>
                        ),
                      )}
                    </ul>
                  </Contributions>
                )}
              <Row className="mb15">
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Reporting dates</TableCell>
                        <TableCell>Portfolio</TableCell>
                        <TableCell>Override</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getDateRows(values).map(({ name, date, number }) => {
                        const portfolioDate = selectedPortfolioDates[number];
                        return (
                          <TableRow key={name}>
                            <TableCell>{name}</TableCell>
                            <TableCell>{portfolioDate}</TableCell>
                            <TableCell>
                              <Link
                                onClick={handleDatePopper('left', number, name)}
                              >
                                {date === noDeadlineText
                                  ? 'Update'
                                  : formattedDate(date)}
                              </Link>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Row>
            </>
          )}
          {activeTab === 'invitation' && (
            <div>
              <SummaryRow>
                <Row>
                  <Col>
                    <span className="r-title">Program name by donor:</span>
                  </Col>
                </Row>
                <TextField
                  placeholder="Program name by donor"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={values.programNameByDonor}
                  onChange={e =>
                    setFieldValue('programNameByDonor', e.target.value)
                  }
                />
                <Row>
                  <Col>
                    <span className="r-title">Organization name:</span>
                  </Col>
                </Row>
                <TextField
                  placeholder="Organization name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  id="field-organization"
                  value={valuesInvitation.organization}
                  error={!!errorsInvitationFields.organization}
                  helperText={errorsInvitationFields.organization}
                  onChange={e =>
                    setFieldInvitation('organization', e.target.value)
                  }
                />
                <Row>
                  <Col>
                    <span className="r-title">Recipient first name:</span>
                  </Col>
                </Row>
                <TextField
                  id="field-firstName"
                  value={valuesInvitation.firstName}
                  onChange={e =>
                    setFieldInvitation('firstName', e.target.value)
                  }
                  placeholder="Recipient first name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!errorsInvitationFields.firstName}
                  helperText={errorsInvitationFields.firstName}
                />
                <Row>
                  <Col>
                    <span className="r-title">Recipient last name:</span>
                  </Col>
                </Row>
                <TextField
                  id="field-lastName"
                  value={valuesInvitation.lastName}
                  onChange={e => setFieldInvitation('lastName', e.target.value)}
                  placeholder="Recipient last Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!errorsInvitationFields.lastName}
                  helperText={errorsInvitationFields.lastName}
                />
                <Row>
                  <Col>
                    <span className="r-title">Recipient email:</span>
                  </Col>
                </Row>
                <TextField
                  id="field-email"
                  value={valuesInvitation.email}
                  onChange={e => setFieldInvitation('email', e.target.value)}
                  placeholder="Recipient email"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={!!errorsInvitationFields.email}
                  helperText={errorsInvitationFields.email}
                />
                <br />
                <br />
                {invitationFinished && (
                  <Row>
                    <SaveChip>
                      <span>Invitation Sent!</span>
                      <DoneIcon />
                    </SaveChip>
                  </Row>
                )}
                <Link
                  disabled={loadingNew || loadingUpdate}
                  onClick={toggleInvitation}
                >
                  Send or Manage Invitations
                </Link>
                {invitationWarning && (
                  <WarningChip>
                    <span>
                      Save the donation first to enable sending invitations.
                    </span>
                    <ErrorOutlineIcon />
                  </WarningChip>
                )}
              </SummaryRow>
            </div>
          )}
          <Row className="mt15">
            <Col>
              {isLoading && (
                <LinearProgress
                  style={{ marginBottom: '15px' }}
                  variant="query"
                />
              )}
            </Col>
          </Row>
          {finished && (
            <Row>
              <SaveChip>
                <span>Donation Saved</span>
                <DoneIcon />
              </SaveChip>
            </Row>
          )}

          {summaryErrors.length > 0 && (
            <ErrorSummary>
              <ErrorSummaryHeader>
                <ErrorOutlineIcon />
                Unable to save donation
              </ErrorSummaryHeader>
              <ErrorSummaryDescription>
                {`Please correct the following ${summaryErrors.length} ${
                  summaryErrors.length === 1 ? 'error' : 'errors'
                }:`}
              </ErrorSummaryDescription>
              {errorTabs.map(({ id, title }) => (
                <ErrorSummaryGroup key={id}>
                  {errorTabs.length > 1 && <h4>{title}</h4>}
                  {summaryErrors
                    .filter(e => e.tab === id)
                    .map(({ key, tab, label, message }) => (
                      <ErrorSummaryRow key={key}>
                        <span>
                          <strong>{label}</strong> — {message}
                        </span>
                        <Link onClick={() => handleFix(tab, key)}>Fix →</Link>
                      </ErrorSummaryRow>
                    ))}
                </ErrorSummaryGroup>
              ))}
            </ErrorSummary>
          )}

          <Row className={isLoading ? '' : 'mt15'}>
            <ColButton>
              <Button
                onClick={handleSave}
                size="large"
                variant="contained"
                color="primary"
                disabled={!hasChanged}
              >
                Save
              </Button>
            </ColButton>
            <ColButton>
              <Button
                onClick={() => setDeleteDialog(true)}
                size="large"
                variant="outlined"
                color="primary"
                disabled={values?.allocations?.length > 0}
              >
                Delete
              </Button>
            </ColButton>
            <ColButton>
              <Button
                onClick={onClose}
                size="large"
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
            </ColButton>
          </Row>
        </Body>
      </Container>
      <ConfirmationDialog
        openDialog={deleteDialog}
        onCloseDialog={() => setDeleteDialog(false)}
        actionButtonOne={handleDelete}
        actionButtonTwo={() => setDeleteDialog(false)}
        textButtonOne="Delete"
        textButtonTwo="Cancel"
        title="Are you sure you want to delete this donation?"
      />
      <ConfirmationDialog
        openDialog={dialogDismiss}
        onCloseDialog={handleCancelDismiss}
        actionButtonOne={handleConfirmDismiss}
        actionButtonTwo={handleCancelDismiss}
        textButtonOne="Confirm"
        textButtonTwo="Cancel"
        title="You have made some changes to this donation, any unsaved changes will be lost."
      />
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </QuickDrawer>
  );
};

export default ContributionDrawer;

const Icon = styled(MuiIcon)`
  position: absolute;
  right: 10px;
  top: 5px;
`;
